import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { SelectOption } from './searchableSelectTypes';

type Direction = 1 | -1;

const findEnabledIndex = <V extends string | number>(
  options: SelectOption<V>[],
  start: number,
  direction: Direction,
) => {
  for (let index = start; index >= 0 && index < options.length; index += direction) {
    if (!options[index].disabled) return index;
  }
  return -1;
};

const findInitialIndex = <V extends string | number>(
  options: SelectOption<V>[],
  preferredValue?: V,
) => {
  const preferredIndex = options.findIndex(
    (option) => option.value === preferredValue && !option.disabled,
  );
  return preferredIndex >= 0 ? preferredIndex : findEnabledIndex(options, 0, 1);
};

interface UseActiveOptionParams<V extends string | number> {
  options: SelectOption<V>[];
  preferredValue?: V;
  getOptionId: (index: number) => string;
  onPick: (option: SelectOption<V>) => void;
  handlesEdgeKeys: boolean;
}

/**
 * The list is replaced, not appended to, when its first option changes (a new search), so that is
 * the only moment the active row resets; a page appended by infinite scroll leaves it alone. The
 * stored index can outlive the row it pointed at, so it is validated on every render.
 */
export const useActiveOption = <V extends string | number>({
  options,
  preferredValue,
  getOptionId,
  onPick,
  handlesEdgeKeys,
}: UseActiveOptionParams<V>) => {
  const [storedIndex, setStoredIndex] = useState(() => findInitialIndex(options, preferredValue));
  const scrollPending = useRef(false);
  const firstValue = options[0]?.value;
  const previousFirstValue = useRef(firstValue);

  const activeIndex = options[storedIndex] && !options[storedIndex].disabled ? storedIndex : -1;

  useEffect(() => {
    if (previousFirstValue.current === firstValue) return;
    previousFirstValue.current = firstValue;
    setStoredIndex(findInitialIndex(options, preferredValue));
  }, [firstValue]);

  useEffect(() => {
    if (!scrollPending.current) return;
    scrollPending.current = false;
    document.getElementById(getOptionId(activeIndex))?.scrollIntoView({ block: 'nearest' });
  }, [storedIndex]);

  const moveTo = (index: number) => {
    if (index < 0 || index === activeIndex) return;
    scrollPending.current = true;
    setStoredIndex(index);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const lastIndex = options.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        moveTo(findEnabledIndex(options, activeIndex + 1, 1));
        break;
      case 'ArrowUp':
        moveTo(findEnabledIndex(options, activeIndex < 0 ? lastIndex : activeIndex - 1, -1));
        break;
      case 'Home':
        if (!handlesEdgeKeys) return;
        moveTo(findEnabledIndex(options, 0, 1));
        break;
      case 'End':
        if (!handlesEdgeKeys) return;
        moveTo(findEnabledIndex(options, lastIndex, -1));
        break;
      case 'Enter':
        if (activeIndex >= 0) onPick(options[activeIndex]);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  return {
    activeIndex,
    activeOptionId: activeIndex >= 0 ? getOptionId(activeIndex) : undefined,
    setActiveIndex: setStoredIndex,
    handleKeyDown,
  };
};
